/* One lifecycle and mutation queue for Concept cart actions and the SKRT gift coordinator.
 * begin/end announce shopper operations, including their debounce period. run only serializes
 * work; the gift coordinator uses run without announcing an external shopper operation.
 */
(function (root) {
  'use strict';
  if (!root || root.skrtCartOperations) return;

  const pending = new Set();
  let sequence = 0;
  let localQueue = Promise.resolve();

  function announce(type, id) {
    root.document.dispatchEvent(new CustomEvent(type, { detail: { id } }));
  }

  function begin() {
    const id = 'skrt-cart-' + Date.now() + '-' + (++sequence);
    pending.add(id);
    announce('skrt:cart-operation-start', id);
    return id;
  }

  function end(id) {
    if (!pending.delete(id)) return;
    announce('skrt:cart-operation-end', id);
  }

  function run(task, signal) {
    if (typeof task !== 'function') return Promise.reject(new TypeError('Cart task must be a function'));
    const waiting = new AbortController();
    let acquired = false;
    let timer;
    let rejectWaiting;
    const aborted = new Promise((resolve, reject) => { rejectWaiting = reject; });
    // Web Locks already rejects on abort; this also bounds waiting in the local queue fallback.
    const onAbort = () => rejectWaiting(waiting.signal.reason || new DOMException('Cart operation canceled', 'AbortError'));
    const cancel = () => waiting.abort(signal && signal.reason);
    waiting.signal.addEventListener('abort', onAbort, { once: true });
    if (signal) {
      if (signal.aborted) cancel();
      else signal.addEventListener('abort', cancel, { once: true });
    }
    timer = root.setTimeout(() => waiting.abort(new DOMException('Cart operation lock timed out', 'AbortError')), 10000);

    function finishWaiting() {
      root.clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', cancel);
      waiting.signal.removeEventListener('abort', onAbort);
    }

    async function execute() {
      if (waiting.signal.aborted) throw waiting.signal.reason || new DOMException('Cart operation canceled', 'AbortError');
      acquired = true;
      finishWaiting();
      return await task();
    }

    let work;
    try {
      if (root.navigator.locks && typeof root.navigator.locks.request === 'function') {
        work = root.navigator.locks.request('skrt-native-gift', { signal: waiting.signal }, execute);
      } else {
        work = localQueue.then(execute, execute);
        // A canceled waiter remains a no-op in the queue. It must never execute its task later.
        localQueue = work.catch(() => {});
      }
    } catch (error) {
      work = Promise.reject(error);
    }
    return Promise.race([work, aborted]).finally(() => {
      if (!acquired) finishWaiting();
    });
  }

  root.skrtCartOperations = Object.freeze({
    begin,
    end,
    run,
    get pendingIds() { return Array.from(pending); }
  });
})(typeof window !== 'undefined' ? window : null);

/* The $0 shipping item is a cart benefit marker. Native delivery rates alone decide
 * eligibility at hosted checkout. It never applies a discount or changes other lines. */
(function (root) {
  'use strict';
  const SOURCE = 'skrt-shipping-reward';
  const THRESHOLD = 6000;

  function rewardLines(cart, config) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
    return cart.items.filter((line) => Number(line.product_id) === Number(config.productId) &&
      Number(line.variant_id) === Number(config.variantId) && typeof line.key === 'string' && line.key);
  }

  function plan(cart, config) {
    const lines = rewardLines(cart, config);
    const idsValid = Number(config.productId) > 0 && Number(config.variantId) > 0;
    const currency = cart.currency && (cart.currency.iso_code || cart.currency);
    if (!Number.isFinite(cart.total_price)) throw new Error('Invalid cart total');
    const markerTotal = lines.reduce((sum, line) => sum + Number(line.final_line_price || 0), 0);
    const hasGoods = cart.items.some((line) => !lines.includes(line) && line.requires_shipping === true && line.quantity > 0);
    const hasSeparateShipping = cart.items.some((line) => line.requires_shipping === true && String(line.vendor || '').toUpperCase() === 'VERNA');
    const eligible = idsValid && config.country === 'US' && currency === 'USD' && hasGoods && !hasSeparateShipping &&
      cart.total_price - markerTotal >= THRESHOLD;
    const updates = {};
    lines.forEach((line, index) => {
      const quantity = eligible && index === 0 ? 1 : 0;
      if (line.quantity !== quantity) updates[line.key] = quantity;
    });
    return { eligible, updates, add: eligible && !lines.length,
      count: cart.items.reduce((sum, line) => sum + (lines.includes(line) ? 0 : Number(line.quantity || 0)), 0) };
  }

  function createController(win, options = {}) {
    const doc = win.document;
    const requestMs = options.requestMs || 8000;
    const debounceMs = options.debounceMs || 120;
    const cooldownMs = options.cooldownMs || 10000;
    let running = null, timer = null, installed = false, readyStarted = false, unsubscribe = null;
    let failures = 0, lastFailure = -Infinity, lastVerified = -Infinity;
    let needsRefresh = false;
    const listeners = [];
    const now = () => win.Date ? win.Date.now() : Date.now();

    function config() {
      if (options.config) return options.config;
      const node = doc.querySelector('[data-skrt-shipping-reward-config]');
      return node ? JSON.parse(node.textContent) : null;
    }

    function urlFor(path) {
      const origin = win.location.origin;
      const candidate = new URL(win.Shopify && win.Shopify.routes && win.Shopify.routes.root || '/', origin);
      const base = candidate.origin === origin ? candidate : new URL('/', origin);
      if (!base.pathname.endsWith('/')) base.pathname += '/';
      const result = new URL(path, base);
      if (result.origin !== origin) throw new Error('Cart endpoint must be same origin');
      return result;
    }

    async function request(path, body, accept = 'application/json') {
      const abort = new AbortController();
      let timeout;
      const expired = new Promise((resolve, reject) => {
        timeout = win.setTimeout(() => { abort.abort(); reject(new Error('Shipping reward request timed out')); }, requestMs);
      });
      const task = (async () => {
        const response = await win.fetch(urlFor(path).href, {
          method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
          headers: { Accept: accept, ...(body ? { 'Content-Type': 'application/json' } : {}) },
          ...(body ? { body: JSON.stringify(body) } : {}), signal: abort.signal
        });
        if (!response.ok) throw new Error('Shipping reward request failed');
        return response.json();
      })();
      try { return await Promise.race([task, expired]); }
      finally { win.clearTimeout(timeout); }
    }

    function updateCounts(cart) {
      const settings = config();
      if (!settings || !cart) return;
      const count = plan(cart, settings).count;
      // Display-only correction. Never alter Shopify's cart payload or analytics events.
      for (const node of doc.querySelectorAll('cart-count')) {
        node.innerText = String(count);
        node.removeAttribute('aria-label');
        node.hidden = count === 0;
      }
    }

    function replacePreservingInputs(current, next) {
      const controls = typeof current.querySelectorAll === 'function' ? Array.from(current.querySelectorAll('input, textarea, select')) : [];
      const focused = doc.activeElement;
      const saved = controls.map((node, index) => ({
        id: node.id, name: node.name, index,
        editable: node.type !== 'hidden' && !(node.name || '').startsWith('updates[') && !node.disabled,
        value: node.value, checked: node.checked,
        focused: node === focused,
        start: node.selectionStart, end: node.selectionEnd
      }));
      current.innerHTML = next.innerHTML;
      const replacements = typeof current.querySelectorAll === 'function' ? Array.from(current.querySelectorAll('input, textarea, select')) : [];
      // CountryProvince rebuilds its province options on country change. Restore
      // that dependency before restoring the selected province, not just select.value.
      saved.sort((left, right) => Number(right.name === 'address[country]') - Number(left.name === 'address[country]'));
      for (const prior of saved) {
        const node = prior.id ? replacements.find((candidate) => candidate.id === prior.id) :
          replacements.filter((candidate) => candidate.name === prior.name)[controls.slice(0, prior.index).filter((candidate) => candidate.name === prior.name).length];
        if (!node) continue;
        if (prior.editable) {
          node.value = prior.value;
          if (typeof prior.checked === 'boolean') node.checked = prior.checked;
          if (prior.name === 'address[country]') node.dispatchEvent(new win.Event('change', { bubbles: true }));
          else if (node.tagName === 'SELECT' && typeof node.onChange === 'function') node.onChange();
        }
        if (prior.focused) {
          node.focus({ preventScroll: true });
          if (prior.start !== null && prior.start !== undefined && typeof node.setSelectionRange === 'function') node.setSelectionRange(prior.start, prior.end);
        }
      }
    }

    async function refreshViews(cart) {
      const operations = win.skrtCartOperations;
      if (operations.pendingIds.length) return false;
      const main = doc.querySelector('main-cart[data-section-id]');
      const drawer = doc.querySelector('cart-drawer[data-section-id]');
      const section = main || drawer;
      if (section) {
        const id = section.getAttribute('data-section-id');
        const path = urlFor(main ? 'cart' : '');
        path.searchParams.set('sections', id);
        // Shopify /cart negotiates ordinary cart JSON when Accept is application/json,
        // even with ?sections=. Ask for HTML so the section-rendering query is honored.
        const sections = await request(path.href, undefined, 'text/html');
        if (typeof sections[id] !== 'string') throw new Error('Cart section unavailable');
        const parsed = new win.DOMParser().parseFromString(sections[id], 'text/html');
        const target = main ? 'shopify-section-' + id : 'MiniCart-' + id;
        const current = doc.getElementById(target);
        const next = parsed.getElementById(target);
        if (!current || !next) throw new Error('Cart section missing');
        // A quantity control starts its debounce before its request enters the queue.
        // Replacing that control would disconnect it and cancel the shopper's edit.
        if (operations.pendingIds.length) return false;
        // Note/attribute/address inputs do not join the quantity-operation queue.
        // Preserve their unsaved values and focus rather than dropping typed customer data.
        replacePreservingInputs(current, next);
      }
      if (operations.pendingIds.length) return false;
      const pubsub = win.theme && win.theme.pubsub;
      if (pubsub && pubsub.PUB_SUB_EVENTS) pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cart });
      // No asynchronous cart:refresh: its native unguarded replacement could cancel
      // a quantity debounce that starts after this controller releases the queue.
      updateCounts(cart);
      return true;
    }

    async function reconcile() {
      if (running) return running;
      const settings = config();
      if (!settings || !Number(settings.variantId) || !Number(settings.productId)) return { status: 'unconfigured' };
      if (failures >= 3 || now() - lastFailure < cooldownMs) return { status: 'cooldown' };
      const operations = win.skrtCartOperations;
      if (!operations || typeof operations.run !== 'function') return { status: 'queue-unavailable' };
      if (operations.pendingIds.length) return { status: 'shopper-operation-pending' };
      running = (async () => {
        try {
          return await operations.run(async () => {
            if (operations.pendingIds.length) return { status: 'shopper-operation-pending' };
            const before = await request('cart.js');
            const action = plan(before, settings);
            // A quantity debounce may start while the fresh cart GET is in flight.
            if (operations.pendingIds.length) return { status: 'shopper-operation-pending' };
            let changed = false;
            if (Object.keys(action.updates).length) {
              await request('cart/update.js', { updates: action.updates });
              changed = true;
            } else if (action.add) {
              await request('cart/add.js', { items: [{ id: Number(settings.variantId), quantity: 1,
                properties: { _skrt_shipping_reward: '1' } }] });
              changed = true;
            }
            const after = changed ? await request('cart.js') : before;
            const verified = plan(after, settings);
            if (verified.add || Object.keys(verified.updates).length) throw new Error('Shipping reward verification failed');
            lastVerified = now();
            failures = 0;
            if (changed || needsRefresh) {
              needsRefresh = true;
              needsRefresh = !(await refreshViews(after));
            }
            else updateCounts(after);
            return { status: changed ? 'updated' : 'clean', eligible: verified.eligible };
          });
        } catch (error) {
          failures += 1;
          lastFailure = now();
          // No blind replay after an uncertain write. Checkout rates remain authoritative.
          return { status: 'failed' };
        }
      })();
      try { return await running; }
      finally { running = null; }
    }

    function schedule(event) {
      const detail = event && (event.detail || event);
      if (detail && detail.source === SOURCE) return;
      if (doc.hidden || failures >= 3 || now() - lastFailure < cooldownMs) return;
      const fresh = detail && detail.cart;
      const settings = config();
      if (settings && fresh && Array.isArray(fresh.items) && Number.isFinite(fresh.total_price)) {
        const action = plan(fresh, settings);
        // Run after Concept's cart-count subscribers without altering their payload.
        Promise.resolve().then(() => updateCounts(fresh));
        if (!action.add && !Object.keys(action.updates).length && !needsRefresh) {
          lastVerified = now();
          win.clearTimeout(timer); timer = null;
          return;
        }
      } else if (!needsRefresh && now() - lastVerified < 500) return;
      win.clearTimeout(timer);
      timer = win.setTimeout(() => { timer = null; void reconcile(); }, debounceMs);
    }

    function listen(target, type, handler, capture) {
      target.addEventListener(type, handler, capture);
      listeners.push(() => target.removeEventListener(type, handler, capture));
    }

    function install() {
      if (installed) return;
      installed = true;
      for (const type of ['skrt:cart-operation-end', 'cart:updated', 'ajaxProduct:added', 'cart:refresh', 'shopify:section:load']) listen(doc, type, schedule);
      listen(doc, 'visibilitychange', () => { if (!doc.hidden) schedule(); });
      listen(win, 'pageshow', (event) => { if (event.persisted) schedule(); });
      const ready = () => {
        if (readyStarted) return;
        readyStarted = true;
        const pubsub = win.theme && win.theme.pubsub;
        if (pubsub && pubsub.PUB_SUB_EVENTS && typeof pubsub.subscribe === 'function') unsubscribe = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, schedule);
        schedule();
      };
      if (doc.readyState !== 'complete') listen(doc, 'DOMContentLoaded', ready);
      else ready();
    }

    function destroy() {
      win.clearTimeout(timer);
      for (const remove of listeners) remove();
      if (typeof unsubscribe === 'function') unsubscribe();
    }
    return Object.freeze({ reconcile, install, destroy, plan: (cart) => plan(cart, config()) });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { plan, rewardLines, createController, SOURCE, THRESHOLD };
    return;
  }
  if (!root || root.__skrtShippingReward) return;
  root.__skrtShippingReward = createController(root);
  root.__skrtShippingReward.install();
})(typeof window === 'undefined' ? null : window);
